/* eslint-disable react-hooks/rules-of-hooks */
import { HTTP_STATUS } from "@api/constant";
import HoroscopeBlock from "@blocks/Horoscope/HoroscopeBlock";
import { LOCALHOST } from "@config/MetaTagsConstants";
import { HoroscopeSectionName } from "@config/SectionConstants";
import MainBlock from "@container/MainBlock";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import Taboola from "@elements/Advertisement/Taboola";
import ApplyParagraphAnnotations from "@elements/ArticleParagraph/ApplyParagraphAnnotations";
import Divider from "@elements/Divider/Divider";
import Image from "@elements/ResponsiveImage/Image";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import { isArrayWithElements } from "@helper/utils";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import type { Route } from "@route-types/Horoscope/+types/Horoscope";
import ErrorPage from "@routes/ErrorPage";
import { fetchHoroscopeData } from "@routes/Horoscope/Horoscope.server";
import NotFound from "@routes/NotFound";
import {
  fetchContext,
  ResponseType,
  type TRouteWithoutRedirect,
  type TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags, MetaTagsProps } from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { HoroscopePayloadResponseType } from "@typings/PagePayloadType";
import { Fragment } from "react/jsx-runtime";
import { ClientLoaderFunctionArgs, useParams } from "react-router";
import { useLoaderData } from "react-router";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    HoroscopePayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function Horoscope() {
  const { zodiac } = useParams();

  const dataLoaderResponse: TRouteWithoutRedirect<
    HoroscopePayloadResponseType,
    string
  > = useLoaderData();
  const adSettings: AdsSettings = useGlobalAdSetting();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message={dataLoaderResponse.payload ?? ""} />;
  }

  const horoscope = dataLoaderResponse.payload;
  const thisZodiac = horoscope.zodiac;
  const thisZodiacImg = thisZodiac ? thisZodiac.img : "";
  const thisZodiacEn = thisZodiac ? thisZodiac.en : "";
  const thisZodiacTa = thisZodiac ? thisZodiac.ta : "";

  const horoscopeDaily = horoscope.daily && horoscope.daily.articleBody;
  const horoscopeWeekly = horoscope.weekly && horoscope.weekly.articleBody;

  const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;

  return (
    <MainContainer
      sectionName={HoroscopeSectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adSettings}
    >
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock
          sectionName={`${HoroscopeSectionName}-${zodiac}`}
          showTitle={false}
        >
          <div className="pb-5">
            <h3 className="w-full font-bold text-1lg mt-0 mb-2 leading-600">
              ராசிபலன்
            </h3>
            <div className="flex flex-wrap -mx-2.5">
              <div className="relative w-full pr-200 pl-6 text-center sm:flex-33 sm:max-w-33">
                <div className="w-32 h-32 mx-auto items-center bg-gradient-to-b from-[#E8E8EF] to-[#F3F3F7] rounded-50 flex justify-center overflow-hidden">
                  <Image
                    srcWidth={150}
                    srcHeight={150}
                    src={thisZodiacImg}
                    alt={thisZodiacEn}
                    className="inline"
                    objectFit="fill"
                  />
                </div>
                <h1 className="text-center mt-0 mb-2 leading-600 text-xl font-bold text-black-200">
                  {thisZodiacTa}
                </h1>
              </div>
              <div className="relative w-full px-6 sm:flex-66 sm:max-w-66">
                <div>
                  {isArrayWithElements(horoscopeDaily) && (
                    <Fragment>
                      <p className="mt-0 mb-4 text-xs font-normal leading-800 text-gray-100">
                        <strong>இன்றைய பலன்:</strong>
                      </p>
                      {horoscopeDaily.map((element, i: number) => {
                        return (
                          element.type == "paragraph" && (
                            <p
                              key={i}
                              className="mt-0 mb-4 text-xs font-normal leading-800 text-gray-100"
                            >
                              {ApplyParagraphAnnotations(
                                element.value,
                                element.annotations,
                              )}
                            </p>
                          )
                        );
                      })}
                    </Fragment>
                  )}
                  {isArrayWithElements(horoscopeWeekly) && (
                    <Fragment>
                      <p className="mt-0 mb-4 text-xs font-normal leading-800 text-gray-100">
                        <strong>வாரப் பலன்:</strong>
                      </p>
                      {horoscopeWeekly.map((element, i: number) => {
                        return (
                          element.type == "paragraph" && (
                            <p
                              key={i}
                              className="mt-0 mb-4 text-xs font-normal leading-800 text-gray-100"
                            >
                              {ApplyParagraphAnnotations(
                                element.value,
                                element.annotations,
                              )}
                            </p>
                          )
                        );
                      })}
                    </Fragment>
                  )}
                </div>
              </div>
            </div>
            <Divider className="my-5 border-b border-gray-2200" />
            <HoroscopeBlock />
            <Divider className="mt-5 border-b border-gray-2200" />
          </div>
          {adSettings.isTaboolaEnabled && (
            <Taboola
              counter={0}
              pathName={`/horoscope/${zodiac}`}
              pageType="article"
              mode="thumbnails-base-widget"
              container="below-article-thumbnails"
              placement="Below Article Thumbnails"
              targetType="mix"
              isFlush={true}
            />
          )}
        </MainBlock>
        <SideBlock
          sectionName={HoroscopeSectionName}
          className="w-full lg:block"
          pageAdTargetValue={pageAdTargetValue}
        />
      </WrapperBlock>
    </MainContainer>
  );
}

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url, LOCALHOST);
  const zodiacRoute: string[] = url.pathname.split("/").filter(Boolean);
  const zodiac = zodiacRoute[1] || "";
  const data = await fetchHoroscopeData(zodiac);

  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };
  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<HoroscopePayloadResponseType, string>
  >;
}
